import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const navLinks = [
  {
    label: "About Us",
    items: [
      { label: "Why We Exist", href: "/why-we-exist" },
      { label: "About the Company", href: "/about" },
      { label: "Founder Story", href: "/founder" },
    ],
  },
  {
    label: "Who We Serve",
    items: [
      { label: "Who We Help", href: "/who-we-help" },
      { label: "Who This Is NOT For", href: "/not-for-everyone" },
    ],
  },
  {
    label: "How We Work",
    items: [
      { label: "How We Help", href: "/how-we-help" },
      { label: "What We Do", href: "/what-we-do" },
      { label: "What We Build", href: "/what-we-build" },
      { label: "Our Process", href: "/process" },
    ],
  },
  {
    label: "Engagement",
    items: [
      { label: "Pricing", href: "/pricing" },
      { label: "Engagement Types", href: "/engagements" },
      { label: "Terms", href: "/terms" },
    ],
  },
  { label: "Case Studies", href: "/case-studies" },
  { label: "Blog", href: "/blog" },
];

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

export const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Check if any child route is active
  const isGroupActive = (items) => {
    return items?.some(item => location.pathname === item.href);
  };

  return (
    <>
      <ScrollToTop />
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <nav className="container-wide">
          <div className="flex h-18 items-center justify-between py-3 gap-8">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-1.5 group flex-shrink-0">
              <div className="w-16 h-16 flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img 
                  src="/logo.png" 
                  alt="Let's Build Startup" 
                  className="w-16 h-16 object-contain rocket-logo"
                />
              </div>
              <div className="flex flex-col">
                <span className="font-serif text-xl font-medium text-foreground group-hover:text-accent transition-colors whitespace-nowrap">
                  Let's Build Startup
                </span>
                <span className="text-[10px] text-muted-foreground tracking-wide">
                  A Gritbyte Labs Venture
                </span>
              </div>
            </Link>

            {/* Desktop Navigation - Tube Style */}
            <div className="hidden lg:flex items-center mx-auto">
              <div className="flex items-center gap-0.5 bg-muted/50 rounded-full px-1.5 py-1 border border-border/50">
                {navLinks.map((link) =>
                  link.items ? (
                    <DropdownMenu key={link.label}>
                      <DropdownMenuTrigger asChild>
                        <button 
                          className={`
                            flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap outline-none
                            focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2
                            ${isGroupActive(link.items) 
                              ? 'bg-background text-foreground shadow-sm' 
                              : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                            }
                          `}
                        >
                          {link.label}
                          <ChevronDown className="h-3.5 w-3.5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="center" className="w-48 mt-2">
                        {link.items.map((item) => (
                          <DropdownMenuItem key={item.href} asChild>
                            <Link
                              to={item.href}
                              className={`w-full ${location.pathname === item.href ? "text-accent font-medium bg-accent/5" : ""}`}
                            >
                              {item.label}
                            </Link>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <Link
                      key={link.label}
                      to={link.href}
                      className={`
                        px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap
                        ${location.pathname === link.href 
                          ? 'bg-background text-foreground shadow-sm' 
                          : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                        }
                      `}
                    >
                      {link.label}
                    </Link>
                  )
                )}
              </div>
            </div>

            {/* CTA */}
            <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
              <Button variant="outline" size="sm" asChild className="rounded-full">
                <Link to="/process">See How We Work</Link>
              </Button>
              <Button variant="accent" size="sm" asChild className="rounded-full">
                <Link to="/contact">Submit Your Wishlist</Link>
              </Button>
            </div>

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[350px]">
                <div className="flex flex-col gap-6 mt-8">
                  {navLinks.map((link) =>
                    link.items ? (
                      <div key={link.label} className="space-y-2">
                        <p className="text-sm font-medium text-muted-foreground">{link.label}</p>
                        <div className="space-y-1 pl-2">
                          {link.items.map((item) => (
                            <Link
                              key={item.href}
                              to={item.href}
                              onClick={() => setMobileOpen(false)}
                              className={`block py-2 text-base ${location.pathname === item.href ? "text-accent font-medium" : "text-foreground hover:text-accent"}`}
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        key={link.label}
                        to={link.href}
                        onClick={() => setMobileOpen(false)}
                        className={`text-base font-medium ${location.pathname === link.href ? "text-accent" : "text-foreground hover:text-accent"}`}
                      >
                        {link.label}
                      </Link>
                    )
                  )}
                  <div className="pt-4 border-t border-border space-y-3">
                    <Button variant="outline" className="w-full rounded-full" asChild>
                      <Link to="/process" onClick={() => setMobileOpen(false)}>See How We Work</Link>
                    </Button>
                    <Button variant="accent" className="w-full rounded-full" asChild>
                      <Link to="/contact" onClick={() => setMobileOpen(false)}>Submit Your Wishlist</Link>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </header>
    </>
  );
};

export default Navbar;
